// Los desenlaces del enrolamiento del candado, cada uno con su color, su icono y su salida. Qué
// dice cada uno lo pone el flujo del proveedor: no todos los equipos se enrolan igual.
import { AlertTriangle, Check, WifiOff } from 'lucide-react';
import { Button, Loading } from '@/kit';
import type { EnrollmentView } from '../../deviceEnrollment';
import {
    failedEnrollmentNotice,
    type EnrollmentNoticeCopy,
    type FailedEnrollmentNotice,
} from '../../enrollmentFlow';

export interface EnrollmentStatusNoticeProps {
    view: EnrollmentView;
    /** Los textos del proveedor del equipo — salen de `enrollmentFlow`, nunca de esta pantalla. */
    copy: EnrollmentNoticeCopy;
    /** El aviso de `FAILED` según el motivo; sin él no se ofrece reintentar. */
    failure?: FailedEnrollmentNotice;
    /** Consulta el estado ahora mismo, sin esperar al siguiente tick del poll. */
    onRecheck: () => void;
    /** Vuelve a abrir el enrolamiento con el mismo IMEI. Solo tras un fallo reintentable. */
    onRetry: () => void;
    recheckPending?: boolean;
    retryPending?: boolean;
}

/** La franja azul de espera, que NO es una falla: se usa en los dos tramos del camino feliz. */
function WaitingNotice({ lead, rest }: { lead: string; rest: string }) {
    return (
        <div
            role="status"
            className="flex items-start gap-2.5 rounded-md border border-info-soft-border bg-info-soft px-3 py-2.5 text-[12.5px] leading-relaxed text-foreground"
        >
            <Loading size="sm" />
            <span>
                <span className="font-semibold">{lead}</span> {rest}
            </span>
        </div>
    );
}

/**
 * 🔴 Ninguna de las tres esperas se pinta igual. `PROVIDER_DOWN` dice que el problema no es del
 * vendedor; `WAITING_LOCK_SIGNAL` dice que ya no hay nada que hacer en el teléfono. Con el cliente
 * enfrente, un spinner idéntico para las tres lo deja sin saber a quién apurar.
 */
export function EnrollmentStatusNotice({
    view,
    copy,
    failure,
    onRecheck,
    onRetry,
    recheckPending = false,
    retryPending = false,
}: EnrollmentStatusNoticeProps) {
    if (view === 'CONFIRMED') {
        return (
            <p
                role="status"
                className="flex items-center gap-2 rounded-md border border-success-soft-border bg-success-soft px-3 py-2 text-[12.5px] font-semibold text-success"
            >
                <Check className="size-3.5 flex-none" strokeWidth={3} aria-hidden />
                {copy.confirmed}
            </p>
        );
    }

    if (view === 'FAILED') {
        const notice = failure ?? failedEnrollmentNotice(copy, null);
        return (
            <div
                role="status"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-[12.5px] leading-relaxed text-destructive"
            >
                <p className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="size-3.5 flex-none" aria-hidden />
                    {notice.title}
                </p>
                <p className="mt-1">{notice.advice}</p>
                {/* Sin reintento, soporte lo reabre desde el backoffice y aquí solo se vuelve a consultar. */}
                {notice.retryable ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        isLoading={retryPending}
                        onClick={onRetry}
                    >
                        Volver a intentar
                    </Button>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        isLoading={recheckPending}
                        onClick={onRecheck}
                    >
                        Consultar de nuevo
                    </Button>
                )}
            </div>
        );
    }

    if (view === 'PROVIDER_DOWN') {
        return (
            <div
                role="status"
                className="rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2.5 text-[12.5px] leading-relaxed text-warning"
            >
                <p className="flex items-center gap-2 font-semibold">
                    <WifiOff className="size-3.5 flex-none" aria-hidden />
                    {copy.providerDown.title}
                </p>
                <p className="mt-1">{copy.providerDown.advice}</p>
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    isLoading={recheckPending}
                    onClick={onRecheck}
                >
                    Consultar de nuevo
                </Button>
            </div>
        );
    }

    // 🔴 Registrado pero sin señal de vida del candado. Es espera normal: pintarlo como error rojo
    // mandaría a rehacer el enrolamiento a alguien que ya terminó bien.
    if (view === 'WAITING_LOCK_SIGNAL') {
        return <WaitingNotice {...copy.waitingLockSignal} />;
    }

    return <WaitingNotice {...copy.waiting} />;
}
